import { useCallback } from "react";
import { t } from "ttag";

import { skipToken, useLazyGetTransformQuery } from "metabase/api";
import { EmptyState } from "metabase/common/components/EmptyState";
import { useMetadataToasts } from "metabase/metadata/hooks";
import { Flex, Skeleton, Stack, Text } from "metabase/ui";
import {
  useGetExternalTransformsQuery,
  useLazyGetWorkspaceTransformQuery,
} from "metabase-enterprise/api";
import { getCheckoutDisabledMessage } from "metabase-enterprise/data-studio/workspaces/utils";
import type {
  DatabaseId,
  ExternalTransform,
  TaggedTransform,
  UnsavedTransform,
  WorkspaceId,
  WorkspaceTransform,
  WorkspaceTransformListItem,
} from "metabase-types/api";
import { isUnsavedTransform } from "metabase-types/api";

import { useWorkspace } from "../WorkspaceProvider";

import { TransformListItem } from "./TransformListItem";
import { TransformListItemMenu } from "./TransformListItemMenu";

/** Item that can be displayed in the workspace transforms list */
type WorkspaceTransformItem = UnsavedTransform | WorkspaceTransformListItem;

function getWorkspaceTransformItemId(
  item: WorkspaceTransformItem,
): string | number {
  if (isUnsavedTransform(item)) {
    return item.id;
  }
  return item.ref_id;
}

type CodeTabProps = {
  activeTransformId?: number | string;
  databaseId: DatabaseId | null | undefined;
  workspaceId: WorkspaceId;
  workspaceTransforms: WorkspaceTransformItem[];
  isLoadingWorkspaceTransforms?: boolean;
  readOnly: boolean;
  onTransformClick: (
    transform: TaggedTransform | WorkspaceTransform | UnsavedTransform,
  ) => void;
};

export const CodeTab = ({
  activeTransformId,
  databaseId,
  workspaceId,
  workspaceTransforms,
  isLoadingWorkspaceTransforms,
  readOnly,
  onTransformClick,
}: CodeTabProps) => {
  const { editedTransforms } = useWorkspace();
  const { sendErrorToast } = useMetadataToasts();

  const {
    data: availableTransforms,
    error,
    isLoading,
  } = useGetExternalTransformsQuery(
    databaseId ? { workspaceId, databaseId } : skipToken,
  );

  const [fetchWorkspaceTransform] = useLazyGetWorkspaceTransformQuery();
  const [fetchTransform] = useLazyGetTransformQuery();

  const handleExternalTransformClick = useCallback(
    async (externalTransform: ExternalTransform) => {
      if (externalTransform.checkout_disabled) {
        return;
      }

      const { data: transform, error } = await fetchTransform(
        externalTransform.id,
        true,
      );

      if (error) {
        sendErrorToast(t`Failed to fetch transform`);
      } else if (transform) {
        const taggedTransform: TaggedTransform = {
          ...transform,
          type: "transform",
        };
        onTransformClick(taggedTransform);
      }
    },
    [fetchTransform, onTransformClick, sendErrorToast],
  );

  const handleWorkspaceTransformItemClick = useCallback(
    async (item: WorkspaceTransformItem) => {
      // Unsaved transforms are stored locally.
      // They should open directly without API fetch.
      if (isUnsavedTransform(item)) {
        // Unsaved transforms should already be opened via the provider
        // This path shouldn't normally be hit, but if it is, we can't
        // call onTransformClick because we don't have a full transform
        return onTransformClick(item);
      }

      const { data: transform, error } = await fetchWorkspaceTransform(
        {
          workspaceId,
          transformId: item.ref_id,
        },
        true,
      );

      if (error) {
        sendErrorToast(t`Failed to fetch transform`);
      } else if (transform) {
        onTransformClick(transform);
      }
    },
    [fetchWorkspaceTransform, workspaceId, onTransformClick, sendErrorToast],
  );

  return (
    <Stack h="100%" gap={0}>
      <Stack
        data-testid="workspace-transforms"
        gap="xs"
        pb="sm"
        style={{
          borderBottom: "1px solid var(--mb-color-border)",
        }}
      >
        <Stack gap={0}>
          <Text fw={600}>{t`Workspace transforms`}</Text>
          {isLoadingWorkspaceTransforms ? (
            <LoadingSkeleton />
          ) : (
            workspaceTransforms.map((item) => {
              const itemId = getWorkspaceTransformItemId(item);
              const isUnsaved = isUnsavedTransform(item);
              const globalId = isUnsaved ? null : item.global_id;
              const isEdited = isUnsaved || editedTransforms.has(itemId);
              const isActive =
                activeTransformId === itemId || activeTransformId === globalId;

              return (
                <TransformListItem
                  key={itemId}
                  name={item.name}
                  icon="pivot_table"
                  fw={600}
                  isActive={isActive}
                  isEdited={isEdited}
                  menu={
                    readOnly ? null : (
                      <TransformListItemMenu
                        transform={item}
                        workspaceId={workspaceId}
                      />
                    )
                  }
                  onClick={() => {
                    handleWorkspaceTransformItemClick(item);
                  }}
                />
              );
            })
          )}
        </Stack>

        {!isLoadingWorkspaceTransforms && workspaceTransforms.length === 0 && (
          <EmptyState message={t`Workspace is empty`} />
        )}
      </Stack>

      <Stack data-testid="mainland-transforms" py="sm" gap="xs">
        <Text fw={600} mt="sm">{t`Available transforms`}</Text>

        {isLoading ? (
          <LoadingSkeleton />
        ) : error ? (
          <Text c="error" size="sm">{t`Failed to load transforms`}</Text>
        ) : (
          <>
            {availableTransforms?.map((transform) => (
              <TransformListItem
                key={transform.id}
                name={transform.name}
                isActive={activeTransformId === transform.id}
                isEdited={editedTransforms.has(transform.id)}
                onClick={() => {
                  handleExternalTransformClick(transform);
                }}
                opacity={transform.checkout_disabled ? 0.5 : 1}
                tooltipLabel={getCheckoutDisabledMessage(
                  transform.checkout_disabled,
                )}
              />
            ))}

            {availableTransforms?.length === 0 && (
              <EmptyState message={t`No available transforms`} />
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
};

function LoadingSkeleton() {
  return (
    <Stack data-testid="loading-indicator" gap="md" py="sm">
      <TransformListItemSkeleton />
      <TransformListItemSkeleton />
      <TransformListItemSkeleton />
    </Stack>
  );
}

function TransformListItemSkeleton() {
  return (
    <Flex align="center" gap="sm" px="md">
      <Skeleton h={14} w={14} circle />
      <Skeleton h={14} w="70%" />
    </Flex>
  );
}
